import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, User, Tag, Eye, Download, Share2, 
  ArrowLeft, BookOpen, Building2, Copy, Check,
  Linkedin, Facebook, Twitter, Mail, ChevronRight,
  FileText, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo publication data for fallback
const DEMO_PUBLICATIONS = {
  "wait-theory-framework-workforce-intelligence": {
    id: "1",
    title: "WAIT Theory: A New Framework for Workforce Intelligence",
    subtitle: "Foundational Research on Workforce AI Integration Theory",
    slug: "wait-theory-framework-workforce-intelligence",
    author: "Dr. Spencer W. Charles, PhD",
    author_title: "Founder & President",
    institution: "Zodiac Workforce Intelligence Institute",
    category: "WAIT Theory",
    tags: ["wait theory", "workforce intelligence", "ai integration", "organizational transformation"],
    abstract: "This foundational paper introduces WAIT Theory (Workforce AI Integration Theory), a comprehensive framework for understanding and measuring the integration of artificial intelligence with human workforce capabilities. The theory proposes new metrics for workforce intelligence and establishes principles for organizational transformation in the age of AI.",
    content_html: `
      <h2>Introduction</h2>
      <p>The modern workforce faces an unprecedented transformation driven by artificial intelligence. Traditional metrics of worker capability—degrees, certifications, years of experience—fail to capture the complex interplay between human intelligence and AI systems. This paper introduces WAIT Theory (Workforce AI Integration Theory), a comprehensive framework designed to address this measurement gap.</p>
      
      <h2>The Credential Fatigue Problem</h2>
      <p>Organizations worldwide report diminishing returns from traditional credentialing systems. A worker with multiple certifications may underperform colleagues with fewer credentials but stronger adaptive capabilities. This phenomenon, which we term "credential fatigue," represents a fundamental disconnect between how we measure and how we should measure workforce capability.</p>
      
      <h2>Core Principles of WAIT Theory</h2>
      <p>WAIT Theory rests on four foundational principles:</p>
      <ol>
        <li><strong>Measurable Intelligence:</strong> Workforce capability must be quantifiable through validated metrics that capture both current performance and adaptive potential.</li>
        <li><strong>Hybrid Alignment:</strong> The value of human workers increasingly depends on their ability to collaborate with and augment AI systems.</li>
        <li><strong>Dynamic Assessment:</strong> Static credentials must give way to continuous measurement of evolving capabilities.</li>
        <li><strong>Organizational Integration:</strong> Individual intelligence gains value only through effective integration with organizational systems.</li>
      </ol>
      
      <h2>The Workforce Intelligence Index (WII)</h2>
      <p>Central to WAIT Theory is the Workforce Intelligence Index (WII), a composite metric that captures multiple dimensions of workforce capability. The WII incorporates:</p>
      <ul>
        <li>Technical competency scores</li>
        <li>AI collaboration proficiency</li>
        <li>Adaptive learning velocity</li>
        <li>Organizational alignment measures</li>
        <li>Innovation contribution indices</li>
      </ul>
      
      <h2>Implications for Organizations</h2>
      <p>Organizations adopting WAIT Theory frameworks report significant improvements in workforce optimization. Early adopters show 23% improvement in project outcomes and 31% reduction in skills-gap related delays.</p>
      
      <h2>Conclusion</h2>
      <p>WAIT Theory provides the foundational framework for a new era of workforce intelligence. By shifting from credential-based to intelligence-based assessment, organizations can better prepare for an AI-integrated future while maximizing the value of human capabilities.</p>
    `,
    featured_image_url: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1200",
    pdf_url: null,
    is_featured: true,
    created_at: "2026-01-15T00:00:00Z",
    updated_at: "2026-01-15T00:00:00Z",
    views: 1247
  },
  "credential-fatigue-hidden-crisis": {
    id: "2",
    title: "Credential Fatigue: The Hidden Crisis in Workforce Development",
    subtitle: "An Analysis of Diminishing Returns in Traditional Credentialing Systems",
    slug: "credential-fatigue-hidden-crisis",
    author: "Dr. Spencer W. Charles, PhD",
    author_title: "Founder & President",
    institution: "Zodiac Workforce Intelligence Institute",
    category: "Credential Fatigue",
    tags: ["credential fatigue", "workforce development", "certifications", "higher education"],
    abstract: "This research examines the phenomenon of credential fatigue — the diminishing value of traditional certifications and degrees in measuring true workforce capability. The paper presents empirical evidence of the gap between credential acquisition and actual job performance, proposing alternative intelligence-based metrics.",
    content_html: `
      <h2>Executive Summary</h2>
      <p>This paper examines the growing disconnect between traditional credentials and actual workforce performance. Through analysis of organizational data and workforce outcomes, we demonstrate that credential accumulation has reached a point of diminishing returns, creating what we term "credential fatigue."</p>
      
      <h2>The Credential Paradox</h2>
      <p>The modern professional faces an interesting paradox: more credentials are available than ever before, yet employers report increasing difficulty finding qualified candidates. This paradox reveals a fundamental flaw in how we conceptualize and measure workforce capability.</p>
      
      <h2>Empirical Evidence</h2>
      <p>Our research analyzed performance data from over 10,000 workers across 15 industries. Key findings include:</p>
      <ul>
        <li>No statistically significant correlation between number of certifications and job performance after the third credential</li>
        <li>47% of hiring managers report credentials as "less important" than five years ago</li>
        <li>Workers with fewer credentials but higher adaptive capacity outperformed highly-credentialed peers by 18%</li>
      </ul>
      
      <h2>Root Causes</h2>
      <p>Several factors contribute to credential fatigue:</p>
      <ol>
        <li><strong>Proliferation:</strong> The credential marketplace has expanded to include thousands of certifications, diluting individual credential value.</li>
        <li><strong>Static Nature:</strong> Credentials represent point-in-time knowledge, failing to capture ongoing capability development.</li>
        <li><strong>Misalignment:</strong> Many credentials test theoretical knowledge rather than practical application ability.</li>
      </ol>
      
      <h2>Toward Intelligence-Based Assessment</h2>
      <p>The solution lies not in abandoning credentials entirely but in supplementing them with continuous, intelligence-based assessment. WAIT Theory provides a framework for this transition, offering metrics that capture the dynamic nature of workforce capability.</p>
      
      <h2>Recommendations</h2>
      <p>Organizations should:</p>
      <ul>
        <li>Audit existing credential requirements for actual predictive validity</li>
        <li>Implement continuous assessment systems alongside credentialing</li>
        <li>Develop internal intelligence metrics aligned with organizational goals</li>
        <li>Shift from credential verification to capability demonstration in hiring</li>
      </ul>
    `,
    featured_image_url: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=1200",
    pdf_url: null,
    is_featured: true,
    created_at: "2026-02-20T00:00:00Z",
    updated_at: "2026-02-20T00:00:00Z",
    views: 892
  }
};

export default function SCFRArticle() {
  const { slug } = useParams();
  const [publication, setPublication] = useState(null);
  const [relatedPublications, setRelatedPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(800);

  // Detect a full standalone HTML document vs an HTML snippet
  const isFullDocument = (html) =>
    typeof html === "string" &&
    /<\s*(html|!doctype html)\b/i.test(html.slice(0, 500));

  // Auto-resize iframe to fit its content (re-checks until layout stabilizes)
  const adjustIframeHeight = () => {
    const frame = iframeRef.current;
    if (!frame) return;
    try {
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc) return;
      const h = Math.max(
        doc.body?.scrollHeight || 0,
        doc.documentElement?.scrollHeight || 0,
        800
      );
      if (Math.abs(h - iframeHeight) > 2) setIframeHeight(h);
    } catch (e) {
      // cross-origin (shouldn't happen with srcDoc) — ignore
    }
  };

  const handleIframeLoad = () => {
    adjustIframeHeight();
    // Re-check a few times as web fonts / images load
    [200, 800, 2000, 4000].forEach((delay) =>
      setTimeout(adjustIframeHeight, delay)
    );
    // Open intra-document links/external links in the parent window
    const frame = iframeRef.current;
    try {
      const doc = frame?.contentDocument;
      if (!doc) return;
      doc.querySelectorAll("a[href]").forEach((a) => {
        const href = a.getAttribute("href") || "";
        if (href.startsWith("http")) {
          a.setAttribute("target", "_blank");
          a.setAttribute("rel", "noopener noreferrer");
        }
      });
    } catch (e) {
      /* ignore */
    }
  };

  // PDF preview modal state
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  useEffect(() => {
    fetchPublication();
  }, [slug]);

  const fetchPublication = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scfr/publications/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPublication(data.publication);
        setRelatedPublications(data.related || []);
      } else {
        // Fallback to demo data
        if (DEMO_PUBLICATIONS[slug]) {
          setPublication(DEMO_PUBLICATIONS[slug]);
          setRelatedPublications([]);
        }
      }
    } catch (error) {
      console.error("Error fetching publication:", error);
      // Fallback to demo data
      if (DEMO_PUBLICATIONS[slug]) {
        setPublication(DEMO_PUBLICATIONS[slug]);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(publication?.title || '');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4">
        <BookOpen className="w-16 h-16 text-white/20 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Publication Not Found</h1>
        <p className="text-white/50 mb-6">The requested article could not be found.</p>
        <Link to="/scfr-journal">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Journal
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="scfr-article-page">
      {/* Hero Section */}
      <section className="relative">
        {/* Background Image */}
        {publication.featured_image_url && (
          <div className="absolute inset-0 h-[50vh]">
            <img 
              src={publication.featured_image_url} 
              alt={publication.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/80 to-slate-950" />
          </div>
        )}
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link 
              to="/scfr-journal"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to SCFR-Journal
            </Link>
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Category Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm">
                {publication.category}
              </span>
              {publication.is_featured && (
                <span className="px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm">
                  Featured
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {publication.title}
            </h1>

            {/* Subtitle */}
            {publication.subtitle && (
              <p className="text-xl text-white/60 mb-6">{publication.subtitle}</p>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-white/50 mb-8">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <div>
                  <span className="text-white font-medium">{publication.author}</span>
                  {publication.author_title && (
                    <span className="text-sm ml-2">({publication.author_title})</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                <span>{publication.institution}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(publication.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                <span>{publication.views?.toLocaleString()} views</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {publication.pdf_url && (
                <>
                  <a
                    href={publication.pdf_url}
                    download
                    data-testid="download-pdf-btn"
                  >
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                      <Download className="w-4 h-4" />
                      Download PDF
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 gap-2"
                    onClick={() => setShowPdfPreview(true)}
                    data-testid="preview-pdf-btn"
                  >
                    <FileText className="w-4 h-4" />
                    Preview PDF
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 gap-2"
                onClick={handleCopyLink}
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[1fr_280px] gap-12">
          {/* Main Content */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="min-w-0"
          >
            {/* Abstract */}
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6 mb-8">
              <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3">Abstract</h2>
              <p className="text-white/70 leading-relaxed">{publication.abstract}</p>
            </div>

            {/* Article Body */}
            {isFullDocument(publication.content_html) ? (
              <div className="bg-white rounded-xl overflow-hidden border border-white/[0.08] shadow-2xl">
                <iframe
                  ref={iframeRef}
                  title={publication.title}
                  srcDoc={publication.content_html}
                  onLoad={handleIframeLoad}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  style={{ width: "100%", height: `${iframeHeight}px`, border: 0, display: "block" }}
                  data-testid="article-iframe"
                />
              </div>
            ) : (
              <div 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-white/10 prose-h2:pb-3
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-white/70 prose-p:leading-relaxed prose-p:mb-6
                  prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white prose-strong:font-semibold
                  prose-ul:text-white/70 prose-ol:text-white/70
                  prose-li:mb-2
                  prose-blockquote:border-l-blue-500 prose-blockquote:bg-white/[0.02] prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg"
                dangerouslySetInnerHTML={{ __html: publication.content_html }}
                data-testid="article-html"
              />
            )}
            {/* Tags */}
            {publication.tags && publication.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="w-5 h-5 text-white/50" />
                  <span className="text-white/50 font-medium">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {publication.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/60 text-sm capitalize"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Citation */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Cite This Article</h3>
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-4 font-mono text-sm text-white/60">
                {publication.author} ({new Date(publication.created_at).getFullYear()}). {publication.title}. <em>SCFR-Journal</em>, Zodiac Workforce Intelligence Institute.
              </div>
            </div>
          </motion.article>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            {/* Share */}
            <div className="sticky top-24">
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6 mb-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-blue-400" />
                  Share
                </h3>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 hover:bg-blue-600 rounded-lg transition-colors"
                  >
                    <Linkedin className="w-5 h-5 text-white" />
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 hover:bg-blue-400 rounded-lg transition-colors"
                  >
                    <Twitter className="w-5 h-5 text-white" />
                  </a>
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/5 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                  <a 
                    href={`mailto:?subject=${shareTitle}&body=${shareUrl}`}
                    className="p-3 bg-white/5 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <Mail className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>

              {/* Related */}
              {relatedPublications.length > 0 && (
                <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-4">Related Publications</h3>
                  <div className="space-y-4">
                    {relatedPublications.map((related) => (
                      <Link 
                        key={related.id}
                        to={`/scfr/${related.slug}`}
                        className="group block"
                      >
                        <h4 className="text-white/80 text-sm font-medium group-hover:text-blue-400 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <p className="text-white/40 text-xs mt-1">{related.author}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* PDF Preview Modal */}
      {showPdfPreview && publication.pdf_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col"
          data-testid="pdf-preview-modal"
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-slate-950/80">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <h3 className="text-white font-medium truncate">{publication.title} — PDF</h3>
            </div>
            <div className="flex items-center gap-2">
              <a href={publication.pdf_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Open in tab
                </Button>
              </a>
              <a href={publication.pdf_url} download>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </a>
              <Button
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white"
                onClick={() => setShowPdfPreview(false)}
              >
                Close
              </Button>
            </div>
          </div>
          <iframe
            title="PDF Preview"
            src={publication.pdf_url}
            className="flex-1 w-full bg-white"
            data-testid="pdf-preview-iframe"
          />
        </div>
      )}

      {/* Back to Journal CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-white/5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/scfr-journal">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to SCFR-Journal
            </Button>
          </Link>
          <Link to="/contact">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              Submit Your Research
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
